/**
 * The shell around the lobby pages (not the board), in Poroboard's look (runebound-app `poroboard/builder`
 * BoardChrome): the 57px bar with the home button (runebound.gg), the "Runebound Play" name, Play and Settings as
 * one pill, and the account at its end; live friend-invite toasts on every signed-in page (docs/design/05 §8).
 */
import {
  AppBar,
  BAR_BORDER,
  BrandTitle,
  FLOAT,
  HomeButton,
  LOOK,
  MENU_ITEM,
  PILL,
  PrimaryButton,
  WELL,
  cn,
} from '@play/ui';
import { Link, useRouterState } from '@tanstack/react-router';
import { ChevronDown, ExternalLink, LogIn, LogOut, Settings, Swords } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth/auth';
import { SignInDialog, openSignIn } from '../auth/SignInDialog';
import { env } from '../lib/env';
import { InviteToasts } from './InviteToasts';
import { RUNEBOUND_URL } from './site';

function NavLink({
  to,
  children,
  className,
}: {
  to: '/play' | '/settings' | '/sandbox';
  children: ReactNode;
  className?: string;
}) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const active = path === to || (to === '/play' && (path === '/' || path.startsWith('/r/')));
  return (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'h-[32px] px-3.5 items-center text-[13px] font-medium whitespace-nowrap transition-colors hover:bg-white/[0.06]',
        className ?? 'inline-flex',
      )}
      style={{ color: active ? '#fff' : LOOK.sub, background: active ? 'rgba(117,70,255,0.22)' : undefined }}
    >
      {children}
    </Link>
  );
}

/** The bar's start: home (runebound.gg) and the name. */
export function ShellBrand() {
  return (
    <>
      <HomeButton href={RUNEBOUND_URL} label="Runebound home" />
      {/* (phones: the icon tile only, so the bar fits 360 px) */}
      <Link to="/" aria-label="Runebound Play home" className="shrink-0 max-sm:[&_.font-display]:hidden">
        <BrandTitle icon={<Swords size={15} />} lead="Runebound " holo="Play" />
      </Link>
    </>
  );
}

/** Who is signed in, at the end of the bar: "Sign in", or the avatar with its menu. */
function Account() {
  const { user, loading, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (loading) return <span className="w-[38px] h-[38px] rounded-full shrink-0" style={{ background: LOOK.raised }} />;
  if (!user) {
    return (
      <PrimaryButton className="shrink-0" onClick={openSignIn}>
        <LogIn size={15} /> Sign in
      </PrimaryButton>
    );
  }
  const meta = user.user_metadata as { full_name?: string; name?: string; avatar_url?: string } | undefined;
  const name = meta?.full_name ?? meta?.name ?? user.email ?? 'Player';
  const initial = name.slice(0, 1).toUpperCase();
  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        onClick={() => {
          setOpen((o) => !o);
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu for ${name}`}
        className="h-[38px] pl-1 pr-2 inline-flex items-center gap-2 rounded-full transition-colors hover:bg-white/[0.05]"
      >
        {meta?.avatar_url ? (
          <img
            src={meta.avatar_url}
            alt=""
            referrerPolicy="no-referrer"
            className="w-[30px] h-[30px] rounded-full object-cover"
            style={{ boxShadow: `0 0 0 1px ${LOOK.border}` }}
          />
        ) : (
          <span
            className="w-[30px] h-[30px] rounded-full inline-flex items-center justify-center text-[13px] font-bold"
            style={{ background: 'rgba(124,92,255,0.22)', color: '#d6ceff', boxShadow: `0 0 0 1px ${LOOK.border}` }}
          >
            {initial}
          </span>
        )}
        <ChevronDown size={14} color={LOOK.muted} className={cn('transition-transform', open && 'rotate-180')} />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+6px)] z-[80] w-[248px] p-1.5 rounded-[12px]"
          style={FLOAT}
        >
          <div className="px-2.5 pt-1.5 pb-2.5 mb-1 border-b" style={{ borderColor: LOOK.border }}>
            <div className="text-[13.5px] font-semibold truncate" style={{ color: LOOK.text }}>
              {name}
            </div>
            {user.email && user.email !== name ? (
              <div className="text-[12px] truncate" style={{ color: LOOK.muted }}>
                {user.email}
              </div>
            ) : null}
          </div>
          <Link
            role="menuitem"
            to="/settings"
            className={MENU_ITEM}
            style={{ color: LOOK.sub }}
            onClick={() => {
              setOpen(false);
            }}
          >
            <Settings size={15} /> Settings
          </Link>
          <a role="menuitem" href={RUNEBOUND_URL} className={MENU_ITEM} style={{ color: LOOK.sub }}>
            <ExternalLink size={15} /> Runebound
          </a>
          <button
            role="menuitem"
            type="button"
            onClick={() => {
              setOpen(false);
              void signOut();
            }}
            className={MENU_ITEM}
            style={{ color: LOOK.sub }}
          >
            <LogOut size={15} /> Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** The page frame for non-board routes. */
export function AppShell({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  return (
    <div className="min-h-dvh flex flex-col" style={{ background: LOOK.page, color: LOOK.text }}>
      <AppBar
        className="sticky top-0 z-30 bg-[#0C1015]"
        start={<ShellBrand />}
        end={
          <div className="flex items-center gap-2 shrink-0">
            {user ? (
              <Link
                to="/settings"
                data-testid="settings-icon"
                className="sm:hidden w-[34px] h-[34px] rounded-[9px] inline-flex items-center justify-center transition-colors hover:bg-white/[0.06]"
                style={{ ...PILL, color: LOOK.sub }}
                aria-label="Settings"
              >
                <Settings size={16} />
              </Link>
            ) : null}
            <Account />
          </div>
        }
      >
        <nav
          className="inline-flex items-center rounded-[9px] overflow-hidden divide-x divide-[#293442] shrink-0"
          style={PILL}
          aria-label="Main"
        >
          <NavLink to="/play">Play</NavLink>
          {/* Phones: the gear icon on the right replaces the Settings link (the bar must fit 360 px). */}
          <NavLink to="/settings" className="hidden sm:inline-flex">
            Settings
          </NavLink>
          {env.sandbox ? (
            <NavLink to="/sandbox" className="hidden sm:inline-flex">
              Sandbox
            </NavLink>
          ) : null}
        </nav>
      </AppBar>
      <main className="flex-1 w-full max-w-[1280px] mx-auto px-4 sm:px-8 pb-16">{children}</main>
      <footer className="w-full border-t" style={{ borderColor: BAR_BORDER, background: WELL }}>
        <p className="max-w-[1280px] mx-auto px-4 sm:px-8 py-5 text-[11px] leading-4" style={{ color: LOOK.faint }}>
          Runebound was created under Riot Games&apos; &ldquo;Legal Jibber Jabber&rdquo; policy using assets owned by
          Riot Games. Riot Games does not endorse or sponsor this project.
        </p>
      </footer>
      {user ? <InviteToasts userId={user.id} /> : null}
      <SignInDialog />
    </div>
  );
}
